import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { Home, ShoppingBag, Wallet, Bell, Target, Calendar, Activity, Radio, ChevronDown, Settings, Package, LogOut, Sparkles, DollarSign, BarChart3, Shield, MapPin, MessageSquare, Handshake, Store, TrendingUp, User, Rocket, Zap, Heart, Users, X, Megaphone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { guidanceAPI } from '../../lib/api';
import { cn } from '../../lib/utils';
import StockTicker from '../shared/LiveTicker';

const ALL_TOOLS = [
  { label: 'Marketing', path: '/marketing', icon: Megaphone, desc: 'Campaigns, email, analytics', mode: ['create', 'sell'] },
  { label: 'AI Studio', path: '/studio', icon: Sparkles, desc: 'Generate music & content', mode: ['create'] },
  { label: 'Creator Rooms', path: '/creators', icon: Radio, desc: 'Go live, earn tips', mode: ['create'] },
  { label: 'Revenue', path: '/revenue', icon: DollarSign, desc: 'Earnings & payouts', mode: ['create', 'sell'] },
  { label: 'Launch Project', path: '/projects/create', icon: Rocket, desc: 'Start funding campaign', mode: ['create'] },
  { label: 'Vendor Dashboard', path: '/vendor', icon: Store, desc: 'Manage products & deals', mode: ['sell'] },
  { label: 'Sales Dashboard', path: '/admin/sales', icon: BarChart3, desc: 'Analytics & reports', mode: ['sell'] },
  { label: 'Portfolio', path: '/investor', icon: TrendingUp, desc: 'Track investments', mode: ['invest'] },
  { label: 'My Orders', path: '/orders', icon: Package, desc: 'Track purchases', mode: ['shop'] },
  { label: 'Deal Rooms', path: '/rooms', icon: Handshake, desc: 'Negotiate deals', mode: ['shop', 'sell'] },
  { label: 'Local Scene', path: '/local-scene', icon: MapPin, desc: 'City-level analytics', mode: ['create', 'invest'] },
  { label: 'Moderation', path: '/moderation', icon: Shield, desc: 'Content review', mode: ['sell'] },
];

const CONTEXT_MODES = [
  { key: 'explore', label: 'Explore', icon: Sparkles, color: 'text-[#b8ff00]', bg: 'bg-[#b8ff00]/10' },
  { key: 'create', label: 'Create', icon: Radio, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { key: 'sell', label: 'Sell', icon: Store, color: 'text-violet-400', bg: 'bg-violet-500/10' },
  { key: 'invest', label: 'Invest', icon: TrendingUp, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  { key: 'shop', label: 'Shop', icon: ShoppingBag, color: 'text-rose-400', bg: 'bg-rose-500/10' },
];

export default function MainLayout() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pulse, setPulse] = useState({});
  const [whisper, setWhisper] = useState(null);
  const [whisperDismissed, setWhisperDismissed] = useState(false);
  const [contextMode, setContextMode] = useState('explore');
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close menu on navigation + set contextual whisper
  useEffect(() => {
    setMenuOpen(false);
    setWhisperDismissed(false);
    
    // Client-side contextual whispers (instant, no API call needed)
    const p = location.pathname;
    const token = localStorage.getItem('mmarketing_token');
    const whisperMap = {
      '/marketplace': { text: 'Flash deals are live — limited time only', icon: 'zap', color: 'red' },
      '/projects': { text: 'Back rising talent from $1 — every legend started somewhere', icon: 'sparkles', color: 'emerald' },
      '/events': { text: 'See your favorite creators live — the energy hits different in person', icon: 'calendar', color: 'amber' },
      '/creators': { text: 'Discover creators — follow, tip, and back their vision', icon: 'sparkles', color: 'violet' },
    };
    
    if (token && whisperMap[p]) {
      setWhisper(whisperMap[p]);
    } else if (token && p.match(/^\/projects\/[^/]+$/)) {
      setWhisper({ text: 'Like what you see? Check the tiers below to back this project', icon: 'target', color: 'emerald' });
    } else if (token && p.match(/^\/events\/[^/]+$/)) {
      setWhisper({ text: 'Grab your tickets — these shows sell out fast', icon: 'calendar', color: 'amber' });
    } else {
      setWhisper(null);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch nav pulse data periodically + whisper on path change
  const pathname = location.pathname;
  useEffect(() => {
    if (!user) return;
    const fetchPulse = () => {
      guidanceAPI.getNavPulse()
        .then(r => setPulse(r.data || {}))
        .catch(() => {});
    };
    fetchPulse();
    const interval = setInterval(fetchPulse, 30000);
    return () => clearInterval(interval);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Whisper display config
  const WHISPER_ICONS = { sparkles: Sparkles, trending: TrendingUp, zap: Zap, calendar: Calendar, radio: Radio, target: Target, users: Users, heart: Heart };
  const WHISPER_COLORS = {
    emerald: 'from-emerald-500/10 to-emerald-500/0 border-emerald-500/20 text-emerald-300',
    rose: 'from-rose-500/10 to-rose-500/0 border-rose-500/20 text-rose-300',
    amber: 'from-amber-500/10 to-amber-500/0 border-amber-500/20 text-amber-300',
    blue: 'from-blue-500/10 to-blue-500/0 border-blue-500/20 text-blue-300',
    violet: 'from-violet-500/10 to-violet-500/0 border-violet-500/20 text-violet-300',
    red: 'from-red-500/10 to-red-500/0 border-red-500/20 text-red-300',
  };

  const navItems = [
    { path: '/', icon: Home, label: 'Home', pulse: 0 },
    { path: '/marketplace', icon: ShoppingBag, label: 'Shop', pulse: pulse.active_deals || 0, pulseColor: 'bg-red-500' },
    { path: '/projects', icon: Target, label: 'Projects', pulse: pulse.hot_projects || 0, pulseColor: 'bg-[#b8ff00]' },
    { path: '/events', icon: Calendar, label: 'Events', pulse: pulse.upcoming_events || 0, pulseColor: 'bg-amber-400' },
    { path: '/creators', icon: Radio, label: 'Creators', pulse: pulse.live_creators || 0, pulseColor: 'bg-rose-500', isLive: (pulse.live_creators || 0) > 0 },
    { path: user ? '/wallet' : '/login', icon: Wallet, label: 'Wallet', pulse: 0 },
  ];

  // Context-aware tools: in "explore" mode show all, otherwise filter by mode
  const visibleTools = contextMode === 'explore'
    ? ALL_TOOLS
    : ALL_TOOLS.filter(t => t.mode.includes(contextMode));

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <StockTicker />

      {/* Header — clean: Logo + Notifications + User menu */}
      <header className="sticky top-0 z-40 glass border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 bg-gradient-to-br from-[#b8ff00] to-[#00ff88] flex items-center justify-center rounded-xl">
              <span className="font-display font-bold text-black text-lg">M</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-display font-bold text-xl">MMarketing</span>
              <span className="text-xs text-zinc-500 block -mt-1">Real-Time Marketplace</span>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            {user && (
              <Link
                to="/alerts"
                className="p-2 hover:bg-zinc-100 rounded-full transition-colors relative"
                data-testid="notifications-btn"
              >
                <Bell size={20} className="text-zinc-600" />
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-[#ff0844] rounded-full border-2 border-white" />
              </Link>
            )}

            {user ? (
              /* User dropdown menu */
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-100 rounded-full transition-colors"
                  data-testid="user-menu-btn"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-[#b8ff00] to-[#00ff88] flex items-center justify-center rounded-full">
                    <span className="text-xs font-bold text-black">{user.name?.[0] || 'U'}</span>
                  </div>
                  <span className="text-sm font-semibold hidden sm:block">{user.name?.split(' ')[0]}</span>
                  <ChevronDown size={14} className={cn("text-zinc-400 transition-transform", menuOpen && "rotate-180")} />
                </button>

                {menuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-zinc-200 overflow-hidden z-50" data-testid="user-dropdown">
                    {/* User info */}
                    <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50">
                      <p className="text-sm font-bold text-zinc-900">{user.name}</p>
                      <p className="text-xs text-zinc-500">{user.email}</p>
                    </div>

                    {/* Context Mode Switcher */}
                    <div className="px-3 py-2 border-b border-zinc-100">
                      <p className="px-1 text-[9px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">I want to</p>
                      <div className="flex gap-1 flex-wrap">
                        {CONTEXT_MODES.map(m => {
                          const MIcon = m.icon;
                          const active = contextMode === m.key;
                          return (
                            <button
                              key={m.key}
                              onClick={() => setContextMode(m.key)}
                              className={cn(
                                "flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold transition-all",
                                active ? `${m.bg} ${m.color} ring-1 ring-current/20` : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"
                              )}
                              data-testid={`mode-${m.key}`}
                            >
                              <MIcon size={11} />
                              {m.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Tools for current mode */}
                    <div className="px-2 py-2 border-b border-zinc-100 max-h-56 overflow-y-auto">
                      <p className="px-2 text-[9px] uppercase tracking-widest text-zinc-400 font-bold mb-1">
                        {contextMode === 'explore' ? 'All Tools' : `${CONTEXT_MODES.find(m => m.key === contextMode)?.label} Tools`}
                      </p>
                      {visibleTools.map(tool => {
                        const Icon = tool.icon;
                        const isActive = location.pathname === tool.path;
                        return (
                          <Link
                            key={tool.path}
                            to={tool.path}
                            className={cn(
                              "flex items-center gap-3 px-2 py-2 rounded-lg transition-colors",
                              isActive ? "bg-zinc-100 text-zinc-900" : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                            )}
                            data-testid={`menu-${tool.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <Icon size={16} className={isActive ? "text-[#00aa55]" : ""} />
                            <div>
                              <p className="text-sm font-medium">{tool.label}</p>
                              <p className="text-[10px] text-zinc-400">{tool.desc}</p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>

                    {/* General links */}
                    <div className="px-2 py-2 border-b border-zinc-100">
                      {[
                        { label: 'Orders', path: '/orders', icon: Package },
                        { label: 'Wallet', path: '/wallet', icon: Wallet },
                        { label: 'Settings', path: '/settings', icon: Settings },
                      ].map(item => {
                        const Icon = item.icon;
                        return (
                          <Link key={item.path} to={item.path} className="flex items-center gap-3 px-2 py-2 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors" data-testid={`menu-${item.label.toLowerCase()}`}>
                            <Icon size={16} />
                            <span className="text-sm">{item.label}</span>
                          </Link>
                        );
                      })}
                      {(user?.is_superadmin || user?.email === 'melvin@mmarketing.biz') && (
                        <Link to="/superadmin" className="flex items-center gap-3 px-2 py-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors font-bold" data-testid="menu-superadmin">
                          <Shield size={16} />
                          <span className="text-sm">Super Admin</span>
                        </Link>
                      )}
                      {(user?.is_superadmin || user?.is_marketing_team || user?.email === 'melvin@mmarketing.biz') && (
                        <Link to="/suite" className="flex items-center gap-3 px-2 py-2 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors font-bold" data-testid="menu-suite">
                          <Megaphone size={16} />
                          <span className="text-sm">Marketing Suite</span>
                        </Link>
                      )}
                    </div>

                    {/* Logout */}
                    <div className="px-2 py-2">
                      <button
                        onClick={() => { logout(); setMenuOpen(false); }}
                        className="flex items-center gap-3 px-2 py-2 rounded-lg text-red-500 hover:bg-red-50 w-full transition-colors"
                        data-testid="logout-btn"
                      >
                        <LogOut size={16} />
                        <span className="text-sm font-medium">Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="px-6 py-2.5 bg-gradient-to-r from-[#b8ff00] to-[#00ff88] text-black font-bold text-sm rounded-full hover:shadow-lg hover:shadow-[#b8ff00]/30 transition-all"
                data-testid="login-btn"
              >
                Get Started
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pb-28 min-h-screen">
        {/* Journey Whisper */}
        {whisper && !whisperDismissed && user && (() => {
          const WIcon = WHISPER_ICONS[whisper.icon] || Sparkles;
          const wColor = WHISPER_COLORS[whisper.color] || WHISPER_COLORS.emerald;
          return (
            <div className={`bg-gradient-to-r ${wColor} border-b`} data-testid="journey-whisper">
              <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <WIcon size={14} className="flex-shrink-0 opacity-80" />
                  <p className="text-xs font-medium opacity-90 truncate">{whisper.text}</p>
                </div>
                <button onClick={() => setWhisperDismissed(true)} className="text-xs opacity-40 hover:opacity-80 transition-opacity flex-shrink-0 px-1" data-testid="whisper-dismiss">
                  <X size={12} />
                </button>
              </div>
            </div>
          );
        })()}
        <Outlet />
      </main>

      {/* Bottom Navigation */}
      <nav className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 ${pathname.startsWith('/suite') ? 'hidden' : ''}`}>
        <div className="nav-pill px-2 py-2 flex items-center gap-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            const showPulse = user && item.pulse > 0 && !isActive;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "relative flex items-center gap-2 px-4 py-2.5 transition-all font-medium text-sm",
                  isActive
                    ? "nav-item-active"
                    : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-full"
                )}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <span className="relative">
                  <Icon size={18} strokeWidth={isActive ? 2.5 : 2} />
                  {showPulse && (
                    <span className={cn(
                      "absolute -top-1 -right-1.5 min-w-[14px] h-[14px] flex items-center justify-center rounded-full text-[8px] font-bold text-white px-0.5",
                      item.pulseColor || 'bg-zinc-500',
                      item.isLive && "animate-pulse"
                    )} data-testid={`pulse-${item.label.toLowerCase()}`}>
                      {item.pulse > 9 ? '9+' : item.pulse}
                    </span>
                  )}
                </span>
                <span className={cn("hidden md:block", isActive && "font-semibold")}>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
